import { inject } from "@ember/service"
import Component from "@glimmer/component"
import type { StandardSetSummary } from "@shared/models/csp-jurisdiction"
import type EmberStore from "cc-frontend/services/ember-store"
import type Rpc from "cc-frontend/services/rpc"
import { isEmpty, isString } from "lodash-es"
import * as R from "remeda"
import { tracked } from "tracked-built-ins"

interface Signature {
  Args: {
    subject: string
    status: string
    standardSets: Array<StandardSetSummary>
    jurisdictionId: string
    index: number
  }
}

export default class AdminCspJurisdictionSubject extends Component<Signature> {
  @inject("ember-store") declare store: EmberStore
  @inject declare rpc: Rpc

  @tracked draftSubjectValue: string | null = null
  @tracked isSaving: boolean = false
  @tracked isMenuOpen: boolean = false

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen
  }

  get standardSetIds() {
    return R.map(this.args.standardSets, (standardSet) => standardSet.id)
  }
  get isEditing() {
    return this.draftSubjectValue && this.draftSubjectValue !== this.args.subject
  }

  monitorKeys = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      this.changeSubject(this.standardSetIds, this.draftSubjectValue)
      e.preventDefault()
    } else if (e.key === "Escape") {
      this.draftSubjectValue = this.args.subject
      ;(e.target as HTMLElement).blur()
    }
  }

  cancelEditing = () => {
    this.draftSubjectValue = this.args.subject
  }

  get subject() {
    return this.draftSubjectValue ?? this.args.subject
  }

  changeSubject = (standardSetIds, draftSubjectValue) => {
    if (typeof draftSubjectValue === "string") {
      this.isSaving = true
      this.rpc
        .admin_csp_edit_set({
          fieldMask: ["subject"],
          standardSetIds: standardSetIds,
          subject: draftSubjectValue,
        })
        .then((response) => {
          this.isSaving = false
          if (response.responseOk === false) {
            console.log(response)
            window.alert(`Sorry! There was an error! ${response.result.message}`)
          } else {
            this.store.find("standard-jurisdiction", this.args.jurisdictionId, true).then(() => {
              this.draftSubjectValue = null
            })
          }
        })
    }
  }

  changeStatus = (standardSetIds, status) => {
    this.isSaving = true
    this.rpc
      .admin_csp_edit_set({
        fieldMask: ["status"],
        standardSetIds: standardSetIds,
        status: status,
      })
      .then((response) => {
        this.isSaving = false
        if (response.responseOk === false) {
          console.log(response)
          window.alert(`Sorry! There was an error! ${response.result.message}`)
        } else {
          this.store.find("standard-jurisdiction", this.args.jurisdictionId, true)
        }
      })
  }

  changeValidYear = (standardSetIds) => {
    let year = window.prompt("Enter the new valid year")
    if (isString(year) === true && isEmpty(year) === false && year !== null) {
      this.isSaving = true
      this.rpc
        .admin_csp_edit_set({
          fieldMask: ["valid"],
          standardSetIds: standardSetIds,
          valid: year,
        })
        .then((response) => {
          this.isSaving = false
          if (response.responseOk === false) {
            console.log(response)
            window.alert(`Sorry! There was an error! ${response.result.message}`)
          } else {
            this.store.find("standard-jurisdiction", this.args.jurisdictionId, true)
          }
        })
    }
  }
  changeJurisdictionId = (standardSetIds) => {
    let id = window.prompt("Enter the new jurisdiction ID")
    if (isString(id) === true && id !== null && id !== undefined) {
      this.isSaving = true
      this.rpc
        .admin_csp_edit_set({
          fieldMask: ["jurisdictionId"],
          standardSetIds: standardSetIds,
          jurisdictionId: id,
        })
        .then((response) => {
          this.isSaving = false
          if (response.responseOk === false) {
            console.log(response)
            window.alert(`Sorry! There was an error! ${response.result.message}`)
          } else {
            this.store.find("standard-jurisdiction", this.args.jurisdictionId, true)
          }
        })
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Admin::Csp::JurisdictionSubject": typeof AdminCspJurisdictionSubject
  }
}
